import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ModalState {
  newProposal: boolean;
  exportReport: boolean;
  addMilestone: boolean;
  complianceCheck: boolean;
  settings: boolean;
  notifications: boolean;
  profile: boolean;
  newOpportunity: boolean;
}

interface LoadingState {
  export: boolean;
  compliance: boolean;
  proposal: boolean;
  milestone: boolean;
  opportunity: boolean;
}

interface UIState {
  modals: ModalState;
  loading: LoadingState;
  sidebarOpen: boolean;
  notificationsEnabled: boolean;
  pushNotificationsEnabled: boolean;
  emailNotificationsEnabled: boolean;
  sessionTimeout: number;
  twoFactorEnabled: boolean;
  theme: 'light' | 'dark' | 'system';
  openModal: (modal: keyof ModalState) => void;
  closeModal: (modal: keyof ModalState) => void;
  setLoading: (key: keyof LoadingState, value: boolean) => void;
  toggleSidebar: () => void;
  toggleNotifications: () => void;
  togglePushNotifications: () => void;
  toggleEmailNotifications: () => void;
  setSessionTimeout: (minutes: number) => void;
  toggleTwoFactor: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      modals: {
        newProposal: false,
        exportReport: false,
        addMilestone: false,
        complianceCheck: false,
        settings: false,
        notifications: false,
        profile: false,
        newOpportunity: false,
      },
      loading: {
        export: false,
        compliance: false,
        proposal: false,
        milestone: false,
        opportunity: false,
      },
      sidebarOpen: true,
      notificationsEnabled: true,
      pushNotificationsEnabled: false,
      emailNotificationsEnabled: true,
      sessionTimeout: 30,
      twoFactorEnabled: false,
      theme: 'light',

      openModal: (modal) =>
        set((state) => ({
          modals: { ...state.modals, [modal]: true },
        })),

      closeModal: (modal) =>
        set((state) => ({
          modals: { ...state.modals, [modal]: false },
        })),

      setLoading: (key, value) =>
        set((state) => ({
          loading: { ...state.loading, [key]: value },
        })),

      toggleSidebar: () =>
        set((state) => ({
          sidebarOpen: !state.sidebarOpen,
        })),

      toggleNotifications: () =>
        set((state) => ({
          notificationsEnabled: !state.notificationsEnabled,
        })),

      togglePushNotifications: () =>
        set((state) => ({
          pushNotificationsEnabled: !state.pushNotificationsEnabled,
        })),

      toggleEmailNotifications: () =>
        set((state) => ({
          emailNotificationsEnabled: !state.emailNotificationsEnabled,
        })),

      setSessionTimeout: (minutes) =>
        set({
          sessionTimeout: minutes,
        }),

      toggleTwoFactor: () =>
        set((state) => ({
          twoFactorEnabled: !state.twoFactorEnabled,
        })),

      setTheme: (theme) =>
        set({
          theme,
        }),
    }),
    {
      name: 'bidzora-ui-store',
      partialize: (state) => ({
        notificationsEnabled: state.notificationsEnabled,
        pushNotificationsEnabled: state.pushNotificationsEnabled,
        emailNotificationsEnabled: state.emailNotificationsEnabled,
        sessionTimeout: state.sessionTimeout,
        twoFactorEnabled: state.twoFactorEnabled,
        theme: state.theme,
      }),
    }
  )
);